import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { HeroBackground, PageHeader } from '../components';

interface CommunityMember {
  name: string;
  role: string;
  description: string;
}

interface Place {
  name: string;
  type: string;
  description: string;
  features: string[];
  coordinates: string;
}

const memberImages = ['/Job.png', '/Annaig.JPG'];
const placeImages = ['/Chapelle.jpg'];

export const PeoplesPage: React.FC = () => {
  const { t } = useTranslation();

  const communityMembers = (
    t('peoples.members', { returnObjects: true }) as CommunityMember[]
  ).map((member, index) => ({
    ...member,
    id: index + 1,
    image: memberImages[index],
  }));

  const places = (
    t('peoples.places', { returnObjects: true }) as Place[]
  ).map((place, index) => ({
    ...place,
    id: index + 1,
    image: placeImages[index],
  }));

  return (
    <div className='min-h-screen'>
      {/* Hero header cropped to 1/3 */}
      <HeroBackground size='third' />
      
      {/* Page Title */}
      <PageHeader
        title={t('nav.people_places')}
        subtitle={t('subtitles.people_places')}
      />

      <div className='max-w-6xl mx-auto px-5 py-12'>

        {/* Section Les Gens */}
        <section className='mb-20'>

          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {communityMembers.map((member) => (
              <div
                key={member.id}
                className='bg-church-light rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow'
              >
                <div className='h-64 overflow-hidden'>
                  <img
                    src={member.image}
                    alt={member.name}
                    className='w-full h-full object-cover'
                  />
                </div>

                <div className='p-6'>
                  <h3 className='text-church-brown text-xl font-medium mb-2'>
                    {member.name}
                  </h3>
                  <p className='text-church-gold font-semibold mb-3'>
                    {member.role}
                  </p>
                  <p className='text-gray-600 text-sm leading-relaxed'>
                    {member.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section Les Lieux */}
        <section>
          <div className='space-y-8 '>
            {places.map((place, index) => (
              <div
                key={place.id}
                className={`bg-church-light rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div
                  className={`lg:flex ${
                    index % 2 === 1 ? 'flex-row-reverse' : ''
                  }`}
                >
                  <div className='lg:w-1/2'>
                    <img
                      src={place.image}
                      alt={place.name}
                      className='w-full h-64 lg:h-full object-cover'
                    />
                  </div>

                  <div className='lg:w-1/2 p-8'>
                    <div className='flex items-start justify-between mb-4'>
                      <div>
                        <h3 className='text-church-brown text-2xl font-medium mb-2'>
                          {place.name}
                        </h3>
                        <span className='bg-church-gold text-white px-3 py-1 rounded-full text-sm font-semibold'>
                          {place.type}
                        </span>
                      </div>
                    </div>

                    <p className='text-gray-700 leading-relaxed mb-6'>
                      {place.description}
                    </p>

                    <div className='mb-6'>
                      <h4 className='font-semibold text-gray-800 mb-3'>
                        {t('peoples.features_title')}
                      </h4>
                      <ul className='space-y-1'>
                        {place.features.map((feature, featureIndex) => (
                          <li
                            key={featureIndex}
                            className='text-gray-600 text-sm'
                          >
                            • {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className='text-sm text-gray-500 flex items-center gap-1'>
                      <MapPin className='w-4 h-4' /> {place.coordinates}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
